'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Calculator, Settings, Save, Play, Target } from 'lucide-react';

export function SalaryDesign() {
  const [salarySystem, setSalarySystem] = useState('band');
  const [selectedGrade, setSelectedGrade] = useState('L3');

  const salaryBands = [
    { grade: 'L1', min: 3000, mid: 3500, max: 4000, count: 25, avgSalary: 3400 },
    { grade: 'L2', min: 4000, mid: 4750, max: 5500, count: 42, avgSalary: 4600 },
    { grade: 'L3', min: 5500, mid: 6250, max: 7000, count: 38, avgSalary: 6100 },
    { grade: 'L4', min: 7000, mid: 8000, max: 9000, count: 31, avgSalary: 7800 },
    { grade: 'L5', min: 9000, mid: 10500, max: 12000, count: 18, avgSalary: 10200 }
  ];

  const marketData = [
    { grade: 'L1', internal: 3400, market25: 3200, market50: 3600, market75: 4000 },
    { grade: 'L2', internal: 4600, market25: 4400, market50: 4800, market75: 5200 },
    { grade: 'L3', internal: 6100, market25: 5800, market50: 6200, market75: 6600 },
    { grade: 'L4', internal: 7800, market25: 7500, market50: 8000, market75: 8500 },
    { grade: 'L5', internal: 10200, market25: 9800, market50: 10500, market75: 11200 }
  ];

  const simulationData = [
    { year: '2024', currentCost: 1200, projectedCost: 1200 },
    { year: '2025', currentCost: 1248, projectedCost: 1290 },
    { year: '2026', currentCost: 1297, projectedCost: 1385 },
    { year: '2027', currentCost: 1349, projectedCost: 1486 },
    { year: '2028', currentCost: 1403, projectedCost: 1594 }
  ];

  const salaryIncrease = [
    { department: '개발부', current: 285, projected: 310, increase: 8.8 },
    { department: '영업부', current: 230, projected: 250, increase: 8.7 },
    { department: '마케팅부', current: 180, projected: 195, increase: 8.3 },
    { department: '인사부', current: 120, projected: 130, increase: 8.3 },
    { department: '재무부', current: 95, projected: 103, increase: 8.4 }
  ];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold">직무급 설계</h1>
          <p className="text-xs sm:text-sm text-gray-600">공정하고 경쟁력 있는 급여체계 설계</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="text-xs sm:text-sm">
            <Save className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">설계안 </span>저장
          </Button>
          <Button size="sm" className="text-xs sm:text-sm">
            <Play className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            시뮬레이션
          </Button>
        </div>
      </div>

      <Tabs defaultValue="system" className="space-y-4 sm:space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="system" className="text-[10px] sm:text-sm px-1 sm:px-3">급여체계</TabsTrigger>
          <TabsTrigger value="bands" className="text-[10px] sm:text-sm px-1 sm:px-3">밴드 관리</TabsTrigger>
          <TabsTrigger value="simulation" className="text-[10px] sm:text-sm px-1 sm:px-3">시뮬레이션</TabsTrigger>
          <TabsTrigger value="market" className="text-[10px] sm:text-sm px-1 sm:px-3">시장 분석</TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>급여체계 유형 선택</span>
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                조직에 적합한 급여체계를 선택하고 설정합니다.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
                <Card
                  className={`p-3 sm:p-4 cursor-pointer transition-colors ${salarySystem === 'individual' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setSalarySystem('individual')}
                >
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                    <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 flex-shrink-0 ${salarySystem === 'individual' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`} />
                    <span className="font-medium text-xs sm:text-sm whitespace-nowrap">개별 직무급</span>
                  </div>
                  <p className="text-[10px] sm:text-sm text-gray-600 mb-3 sm:mb-4">
                    각 직무별로 개별적인 급여를 설정
                  </p>
                  <div className="space-y-1 sm:space-y-2 text-[10px] sm:text-sm">
                    <div className="flex justify-between">
                      <span>유연성</span>
                      <span className="text-green-600">높음</span>
                    </div>
                    <div className="flex justify-between">
                      <span>관리 복잡도</span>
                      <span className="text-red-600">높음</span>
                    </div>
                    <div className="flex justify-between">
                      <span>공정성</span>
                      <span className="text-yellow-600">보통</span>
                    </div>
                  </div>
                </Card>

                <Card
                  className={`p-3 sm:p-4 cursor-pointer transition-colors ${salarySystem === 'grade' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setSalarySystem('grade')}
                >
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                    <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 flex-shrink-0 ${salarySystem === 'grade' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`} />
                    <span className="font-medium text-xs sm:text-sm whitespace-nowrap">직무등급 수당형</span>
                  </div>
                  <p className="text-[10px] sm:text-sm text-gray-600 mb-3 sm:mb-4">
                    기본급 + 직무등급별 수당 지급
                  </p>
                  <div className="space-y-1 sm:space-y-2 text-[10px] sm:text-sm">
                    <div className="flex justify-between">
                      <span>유연성</span>
                      <span className="text-yellow-600">보통</span>
                    </div>
                    <div className="flex justify-between">
                      <span>관리 복잡도</span>
                      <span className="text-yellow-600">보통</span>
                    </div>
                    <div className="flex justify-between">
                      <span>공정성</span>
                      <span className="text-green-600">높음</span>
                    </div>
                  </div>
                </Card>

                <Card
                  className={`p-3 sm:p-4 cursor-pointer transition-colors ${salarySystem === 'band' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setSalarySystem('band')}
                >
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                    <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 flex-shrink-0 ${salarySystem === 'band' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`} />
                    <span className="font-medium text-xs sm:text-sm whitespace-nowrap">범위 직무급(밴드)</span>
                  </div>
                  <p className="text-[10px] sm:text-sm text-gray-600 mb-3 sm:mb-4">
                    직무등급별 급여 범위 설정
                  </p>
                  <div className="space-y-1 sm:space-y-2 text-[10px] sm:text-sm">
                    <div className="flex justify-between">
                      <span>유연성</span>
                      <span className="text-green-600">높음</span>
                    </div>
                    <div className="flex justify-between">
                      <span>관리 복잡도</span>
                      <span className="text-green-600">낮음</span>
                    </div>
                    <div className="flex justify-between">
                      <span>공정성</span>
                      <span className="text-green-600">높음</span>
                    </div>
                  </div>
                </Card>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">급여체계 기본 설정</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <Label htmlFor="increase-rate" className="text-xs sm:text-sm">연간 인상률 (%)</Label>
                    <Input id="increase-rate" placeholder="예: 3.5" className="text-sm mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="performance-budget" className="text-xs sm:text-sm">성과급 예산 (%)</Label>
                    <Input id="performance-budget" placeholder="예: 10" className="text-sm mt-1" />
                  </div>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <Label htmlFor="market-position" className="text-xs sm:text-sm">시장 대비 목표 포지션</Label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="선택하세요" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25th percentile</SelectItem>
                        <SelectItem value="50">50th percentile (중간값)</SelectItem>
                        <SelectItem value="75">75th percentile</SelectItem>
                        <SelectItem value="90">90th percentile</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="budget-limit" className="text-xs sm:text-sm">총 인건비 예산 한도 (억원)</Label>
                    <Input id="budget-limit" placeholder="예: 100" className="text-sm mt-1" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bands" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">급여 밴드 테이블</CardTitle>
                <CardDescription className="text-xs sm:text-sm">직무등급별 급여 범위 설정</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap text-xs sm:text-sm">등급</TableHead>
                        <TableHead className="whitespace-nowrap text-xs sm:text-sm">최소</TableHead>
                        <TableHead className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">중간값</TableHead>
                        <TableHead className="whitespace-nowrap text-xs sm:text-sm">최대</TableHead>
                        <TableHead className="whitespace-nowrap text-xs sm:text-sm">인원</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salaryBands.map((band) => (
                        <TableRow
                          key={band.grade}
                          className={`cursor-pointer ${selectedGrade === band.grade ? 'bg-blue-50' : ''}`}
                          onClick={() => setSelectedGrade(band.grade)}
                        >
                          <TableCell>
                            <Badge variant={selectedGrade === band.grade ? 'default' : 'outline'} className="text-[10px] sm:text-xs">
                              {band.grade}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs sm:text-sm">{band.min}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">{band.mid}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs sm:text-sm">{band.max}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs sm:text-sm">{band.count}명</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">급여 밴드 시각화</CardTitle>
                <CardDescription className="text-xs sm:text-sm">등급별 급여 범위 및 분포</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={salaryBands}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="grade" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => [value + '만원', '']} />
                    <Bar dataKey="min" stackId="a" fill="#e2e8f0" name="최소" />
                    <Bar dataKey="mid" stackId="b" fill="#3b82f6" name="중간값" />
                    <Bar dataKey="max" stackId="a" fill="#cbd5e1" name="최대" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">{selectedGrade} 등급 상세 설정</CardTitle>
              <CardDescription className="text-xs sm:text-sm">선택된 등급의 세부 급여 설정</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <Label htmlFor="min-salary" className="text-xs sm:text-sm">최소 급여 (만원)</Label>
                    <Input
                      id="min-salary"
                      value={salaryBands.find(b => b.grade === selectedGrade)?.min || ''}
                      readOnly
                      className="text-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="progression" className="text-xs sm:text-sm">승급 요건</Label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="선택하세요" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="time">근속 기간</SelectItem>
                        <SelectItem value="performance">성과 평가</SelectItem>
                        <SelectItem value="competency">역량 평가</SelectItem>
                        <SelectItem value="mixed">혼합</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <Label htmlFor="mid-salary" className="text-xs sm:text-sm">중간값 급여 (만원)</Label>
                    <Input
                      id="mid-salary"
                      value={salaryBands.find(b => b.grade === selectedGrade)?.mid || ''}
                      readOnly
                      className="text-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="min-period" className="text-xs sm:text-sm">최소 재직 기간 (개월)</Label>
                    <Input id="min-period" placeholder="예: 12" className="text-sm mt-1" />
                  </div>
                </div>
                <div className="space-y-3 sm:space-y-4 sm:col-span-2 md:col-span-1">
                  <div>
                    <Label htmlFor="max-salary" className="text-xs sm:text-sm">최대 급여 (만원)</Label>
                    <Input
                      id="max-salary"
                      value={salaryBands.find(b => b.grade === selectedGrade)?.max || ''}
                      readOnly
                      className="text-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="performance-weight" className="text-xs sm:text-sm">성과급 비중 (%)</Label>
                    <Input id="performance-weight" placeholder="예: 15" className="text-sm mt-1" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="simulation" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                  <Calculator className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>인건비 시뮬레이션</span>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">급여체계 변경에 따른 인건비 영향 분석</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={simulationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => [value + '억원', '']} />
                    <Area
                      type="monotone"
                      dataKey="currentCost"
                      stackId="1"
                      stroke="#82ca9d"
                      fill="#82ca9d"
                      name="현재 체계"
                    />
                    <Area
                      type="monotone"
                      dataKey="projectedCost"
                      stackId="2"
                      stroke="#8884d8"
                      fill="#8884d8"
                      name="신규 체계"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">부서별 인건비 변화</CardTitle>
                <CardDescription className="text-xs sm:text-sm">신규 급여체계 적용 시 부서별 영향</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="space-y-3 sm:space-y-4">
                  {salaryIncrease.map((dept) => (
                    <div key={dept.department} className="space-y-1 sm:space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-xs sm:text-sm whitespace-nowrap">{dept.department}</span>
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <span className="text-[10px] sm:text-sm text-gray-500 whitespace-nowrap">
                            {dept.current}억 → {dept.projected}억
                          </span>
                          <Badge
                            variant={dept.increase > 8.5 ? 'destructive' : 'default'}
                            className="text-[10px] sm:text-xs"
                          >
                            +{dept.increase}%
                          </Badge>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                        <div
                          className="bg-blue-600 h-1.5 sm:h-2 rounded-full"
                          style={{ width: `${(dept.increase / 10) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">시뮬레이션 시나리오</CardTitle>
              <CardDescription className="text-xs sm:text-sm">다양한 조건에서의 인건비 영향 분석</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                <Card className="p-3 sm:p-4">
                  <div className="text-center space-y-1 sm:space-y-2">
                    <h4 className="font-medium text-xs sm:text-sm">보수적 시나리오</h4>
                    <div className="text-xl sm:text-2xl font-bold text-green-600">+5.2%</div>
                    <p className="text-[10px] sm:text-sm text-gray-600">연간 인건비 증가율</p>
                    <div className="text-[10px] sm:text-xs text-gray-500">
                      • 최소 인상률 적용<br/>
                      • 성과급 10% 수준
                    </div>
                  </div>
                </Card>

                <Card className="p-3 sm:p-4 ring-2 ring-blue-500">
                  <div className="text-center space-y-1 sm:space-y-2">
                    <h4 className="font-medium text-xs sm:text-sm">기본 시나리오</h4>
                    <div className="text-xl sm:text-2xl font-bold text-blue-600">+8.4%</div>
                    <p className="text-[10px] sm:text-sm text-gray-600">연간 인건비 증가율</p>
                    <div className="text-[10px] sm:text-xs text-gray-500">
                      • 시장 중간값 수준<br/>
                      • 성과급 15% 수준
                    </div>
                  </div>
                </Card>

                <Card className="p-3 sm:p-4">
                  <div className="text-center space-y-1 sm:space-y-2">
                    <h4 className="font-medium text-xs sm:text-sm">적극적 시나리오</h4>
                    <div className="text-xl sm:text-2xl font-bold text-orange-600">+12.1%</div>
                    <p className="text-[10px] sm:text-sm text-gray-600">연간 인건비 증가율</p>
                    <div className="text-[10px] sm:text-xs text-gray-500">
                      • 시장 상위권 수준<br/>
                      • 성과급 20% 수준
                    </div>
                  </div>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="market" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                <Target className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>시장 급여 비교 분석</span>
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">외부 시장 대비 내부 급여 수준 분석</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={marketData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(value) => [value + '만원', '']} />
                  <Bar dataKey="market25" fill="#f3f4f6" name="시장 25%" />
                  <Bar dataKey="market50" fill="#d1d5db" name="시장 50%" />
                  <Bar dataKey="market75" fill="#9ca3af" name="시장 75%" />
                  <Bar dataKey="internal" fill="#3b82f6" name="우리 회사" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">경쟁력 지수</CardTitle>
                <CardDescription className="text-xs sm:text-sm">시장 대비 급여 경쟁력</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="space-y-3 sm:space-y-4">
                  {marketData.map((data) => {
                    const competitiveness = ((data.internal / data.market50) * 100).toFixed(1);
                    const isCompetitive = data.internal >= data.market50;

                    return (
                      <div key={data.grade} className="space-y-1 sm:space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-xs sm:text-sm">{data.grade}</span>
                          <Badge variant={isCompetitive ? 'default' : 'destructive'} className="text-[10px] sm:text-xs">
                            {competitiveness}%
                          </Badge>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                          <div
                            className={`h-1.5 sm:h-2 rounded-full ${isCompetitive ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(Number(competitiveness), 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">시장 데이터 출처</CardTitle>
                <CardDescription className="text-xs sm:text-sm">급여 정보 수집 현황</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="space-y-2 sm:space-y-4">
                  <div className="flex items-center justify-between p-2 sm:p-3 border rounded gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-xs sm:text-sm truncate">IT 업계 급여조사</div>
                      <div className="text-[10px] sm:text-sm text-gray-500">2024년 상반기</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] sm:text-xs flex-shrink-0">127개 기업</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 sm:p-3 border rounded gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-xs sm:text-sm truncate">경영진협의회 자료</div>
                      <div className="text-[10px] sm:text-sm text-gray-500">2024년 4분기</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] sm:text-xs flex-shrink-0">85개 기업</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 sm:p-3 border rounded gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-xs sm:text-sm truncate">공개 채용정보</div>
                      <div className="text-[10px] sm:text-sm text-gray-500">실시간 수집</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] sm:text-xs flex-shrink-0">200+ 공고</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
